import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Moon, Sun, ShieldCheck, UserRound, HeartHandshake, ChevronRight } from 'lucide-react';

import { api } from '../lib/api.js';
import { useApiData } from '../lib/hooks.js';
import { useSession, useToast } from '../lib/store.js';
import { fmtDate, num } from '../lib/format.js';
import { Card, Button, Sheet, Field, NumberInput, TextInput, TextArea, Choice, Spinner, ErrorNote, SectionTitle, PageHeader, Pill } from '../components/ui.jsx';

export default function Settings() {
  const { user, settings, setTheme, setName } = useSession();
  const { data, loading, error, reload } = useApiData('/profile');
  const [sheet, setSheet] = useState(null);

  if (loading && !data) return <Spinner />;
  if (error) return <div className="p-gutter"><ErrorNote error={error} onRetry={reload} /></div>;
  if (!data) return null;

  const { profile, health } = data;
  const theme = settings?.theme || 'light';

  return (
    <div className="max-w-lg mx-auto">
      <PageHeader title={user?.name || 'You'} subtitle={`Started ${fmtDate(profile.start_date)} · target ${fmtDate(profile.target_date)}`} />

      <div className="px-gutter">
        {profile.programme_mode !== 'fatloss' && (
          <Card className="mb-4 bg-warnSoft border-warn/40">
            <Pill tone="warn">{profile.programme_mode === 'health' ? 'Health mode' : 'Maintenance'}</Pill>
            <p className="text-body-sm text-textSoft mt-2">
              {profile.programme_mode === 'health'
                ? 'Nothing is being aimed at weight loss. Targets are off and the app will not suggest changes.'
                : 'Calories are set at maintenance rather than a deficit. Training carries on as normal.'}
            </p>
          </Card>
        )}

        <SectionTitle action={<Button size="sm" variant="ghost" onClick={() => setSheet('targets')}>Change</Button>}>
          Daily targets
        </SectionTitle>
        <Card className="mb-5">
          <dl className="space-y-2.5">
            <Row label="Calories" value={profile.kcal_low ? `${num(profile.kcal_low)} – ${num(profile.kcal_high)}` : 'Not set'} />
            <Row label="Protein" value={profile.protein_g ? `${profile.protein_g}g` : '—'} />
            <Row label="Steps" value={num(profile.step_target)} />
            <Row
              label="Tracking"
              value={profile.tracking_mode === 'detailed' ? `Detailed until ${fmtDate(profile.tracking_until, 'short')}` : 'Light'}
            />
          </dl>
          <p className="text-meta text-textMute mt-3 pt-3 border-t border-edge">
            These are yours. The app only ever proposes changes to them, and a no is the end of it.
          </p>
        </Card>

        <SectionTitle action={<Button size="sm" variant="ghost" onClick={() => setSheet('training')}>Change</Button>}>Training</SectionTitle>
        <Card className="mb-5">
          <dl className="space-y-2.5">
            <Row label="Days" value={profile.training_days.split(',').map((d) => ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][Number(d)]).join(', ')} />
            <Row label="Session length" value={`${profile.session_minutes} min`} />
          </dl>
        </Card>

        <SectionTitle action={<Button size="sm" variant="ghost" onClick={() => setSheet('health')}>Change</Button>}>Health</SectionTitle>
        <Card className="mb-5">
          <dl className="space-y-2.5">
            <Row label="Bones" value={health?.osteoporosis ? 'Osteoporosis' : 'Not recorded'} />
            <Row label="Hormonal" value={health?.hormonal_context || '—'} />
            <Row label="HRT" value={health?.hrt_note || '—'} />
            <Row label="Supplements" value={health?.supplements_note || '—'} />
            <Row label="Migraines" value={health?.migraine_prone ? 'Yes' : 'No'} />
          </dl>
        </Card>

        <SectionTitle>Things that change the plan</SectionTitle>
        <Card className="mb-5">
          <button onClick={() => setSheet('event')} className="w-full flex items-center gap-3 text-left">
            <HeartHandshake size={20} className="text-accent shrink-0" />
            <div className="flex-1">
              <p className="text-body-sm font-semibold">Trying to conceive, treatment or pregnancy</p>
              <p className="text-meta text-textMute">Tell the app and it stops aiming at fat loss straight away.</p>
            </div>
            <ChevronRight size={18} className="text-textMute shrink-0" />
          </button>
        </Card>

        <SectionTitle>App</SectionTitle>
        <Card className="mb-5">
          <div className="space-y-1">
            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="w-full flex items-center gap-3 py-2.5 text-left"
            >
              {theme === 'dark' ? <Moon size={18} className="text-textMute" /> : <Sun size={18} className="text-textMute" />}
              <span className="flex-1 text-body-sm">{theme === 'dark' ? 'Dark' : 'Light'}</span>
              <span className="text-meta text-textMute">tap to switch</span>
            </button>

            <Link to="/safety" className="w-full flex items-center gap-3 py-2.5">
              <ShieldCheck size={18} className="text-textMute" />
              <span className="flex-1 text-body-sm">How this plan is built</span>
              <ChevronRight size={18} className="text-textMute" />
            </Link>

            <button onClick={() => setSheet('name')} className="w-full flex items-center gap-3 py-2.5 text-left">
              <UserRound size={18} className="text-textMute" />
              <span className="flex-1 text-body-sm">What the app calls you</span>
              <span className="text-meta text-textMute">{user?.name || 'not set'}</span>
            </button>
          </div>

          <p className="text-meta text-textMute mt-3 pt-3 border-t border-edge">
            There is no sign-in — this link is private to you. Anyone you send it to can see everything in here, so treat it like the
            page it is.
          </p>
        </Card>

        {profile.goal_note && (
          <Card className="mb-8 bg-accentSoft border-accent/30">
            <p className="text-label uppercase text-textMute mb-2">Why you started</p>
            <p className="text-body text-textSoft italic">&ldquo;{profile.goal_note}&rdquo;</p>
          </Card>
        )}
      </div>

      <TargetsSheet open={sheet === 'targets'} profile={profile} onClose={() => setSheet(null)} onSaved={() => { setSheet(null); reload({ quiet: true }); }} />
      <TrainingSheet open={sheet === 'training'} profile={profile} onClose={() => setSheet(null)} onSaved={() => { setSheet(null); reload({ quiet: true }); }} />
      <HealthSheet open={sheet === 'health'} health={health} onClose={() => setSheet(null)} onSaved={() => { setSheet(null); reload({ quiet: true }); }} />
      <EventSheet open={sheet === 'event'} onClose={() => setSheet(null)} onSaved={() => { setSheet(null); reload({ quiet: true }); }} />
      <NameSheet open={sheet === "name"} name={user?.name} onClose={() => setSheet(null)} onSaved={setName} />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-body-sm text-textSoft shrink-0">{label}</dt>
      <dd className="text-body-sm font-medium text-right">{value}</dd>
    </div>
  );
}

function useSave(onSaved) {
  const { show } = useToast();
  const [busy, setBusy] = useState(false);
  return [
    busy,
    async (payload, path = '/profile', method = 'patch') => {
      setBusy(true);
      try {
        const res = await api[method](path, payload);
        show('Saved.');
        onSaved(res);
      } catch (err) {
        show(err.message, 'crit');
      } finally {
        setBusy(false);
      }
    },
  ];
}

function TargetsSheet({ open, profile, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, save] = useSave(onSaved);
  if (!open) return null;
  const v = (k) => form[k] ?? profile[k] ?? '';

  return (
    <Sheet open onClose={onClose} title="Daily targets">
      <Field label="Calorie range" id="kl" hint="A range you can actually land in beats a lower one you cannot.">
        <div className="flex items-center gap-2">
          <NumberInput id="kl" value={v('kcal_low')} onChange={(e) => setForm({ ...form, kcal_low: e.target.value })} />
          <span className="text-textMute">to</span>
          <NumberInput value={v('kcal_high')} onChange={(e) => setForm({ ...form, kcal_high: e.target.value })} />
        </div>
      </Field>
      <Field label="Protein" id="pg">
        <NumberInput id="pg" suffix="g" value={v('protein_g')} onChange={(e) => setForm({ ...form, protein_g: e.target.value })} />
      </Field>
      <Field label="Steps" id="stt">
        <NumberInput id="stt" value={v('step_target')} onChange={(e) => setForm({ ...form, step_target: e.target.value })} />
      </Field>
      <Button className="w-full" disabled={busy} onClick={() => save(numify(form, ['kcal_low', 'kcal_high', 'protein_g', 'step_target']))}>
        Save
      </Button>
    </Sheet>
  );
}

function TrainingSheet({ open, profile, onClose, onSaved }) {
  const [days, setDays] = useState(profile.training_days.split(',').map(Number));
  const [mins, setMins] = useState(profile.session_minutes);
  const [busy, save] = useSave(onSaved);
  if (!open) return null;

  const toggle = (n) =>
    setDays((d) => (d.includes(n) ? (d.length > 1 ? d.filter((x) => x !== n) : d) : [...d, n].slice(-3).sort((a, b) => a - b)));

  return (
    <Sheet open onClose={onClose} title="Training">
      <Field label="Days" id="tdays" hint="Three a week. Moving them reshuffles the calendar, not the plan.">
        <div className="grid grid-cols-7 gap-1.5">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((label, i) => {
            const n = i + 1;
            const on = days.includes(n);
            return (
              <button
                key={n}
                type="button"
                onClick={() => toggle(n)}
                aria-pressed={on}
                className={`py-2.5 rounded-inner text-body-sm font-medium border ${on ? 'bg-accent text-onAccent border-accent' : 'bg-panel text-textMute border-edge'}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </Field>
      <Field label="Session length" id="tmins">
        <Choice columns={3} value={mins} onChange={setMins} options={[30, 40, 45].map((n) => ({ value: n, label: `${n} min` }))} />
      </Field>
      <Button className="w-full" disabled={busy} onClick={() => save({ training_days: days, session_minutes: mins })}>
        Save
      </Button>
    </Sheet>
  );
}

function HealthSheet({ open, health, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, save] = useSave(onSaved);
  if (!open) return null;
  const v = (k) => form[k] ?? health?.[k] ?? '';

  return (
    <Sheet open onClose={onClose} title="Health">
      <p className="text-meta text-textMute mb-4">For your reference and to shape exercise choice. The app never interprets any of it.</p>
      <Field label="Hormonal context" id="hc">
        <TextArea id="hc" rows={2} value={v('hormonal_context')} onChange={(e) => setForm({ ...form, hormonal_context: e.target.value })} />
      </Field>
      <Field label="HRT" id="hr"><TextInput id="hr" value={v('hrt_note')} onChange={(e) => setForm({ ...form, hrt_note: e.target.value })} /></Field>
      <Field label="Supplements" id="su"><TextInput id="su" value={v('supplements_note')} onChange={(e) => setForm({ ...form, supplements_note: e.target.value })} /></Field>
      <Field label="Migraine triggers" id="mg"><TextArea id="mg" rows={2} value={v('migraine_triggers')} onChange={(e) => setForm({ ...form, migraine_triggers: e.target.value })} /></Field>
      <Field label="Notes from your clinicians" id="cn" hint="Anything you have been told that the plan should respect.">
        <TextArea id="cn" rows={3} value={v('clinician_notes')} onChange={(e) => setForm({ ...form, clinician_notes: e.target.value })} />
      </Field>
      <Button className="w-full" disabled={busy} onClick={() => save(form)}>Save</Button>
    </Sheet>
  );
}

function EventSheet({ open, onClose, onSaved }) {
  const [kind, setKind] = useState('');
  const [note, setNote] = useState('');
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const { show } = useToast();
  if (!open) return null;

  return (
    <Sheet open onClose={onClose} title="Something has changed">
      {message ? (
        <>
          <p className="text-body text-textSoft mb-5">{message}</p>
          <Button className="w-full" onClick={() => { setMessage(null); onSaved(); }}>Understood</Button>
        </>
      ) : (
        <>
          <p className="text-body-sm text-textSoft mb-4">
            If any of these happen, the fat-loss target stops. The app will not carry on regardless, and it will not tell you what to do
            next — that comes from your clinic.
          </p>
          <Field label="What has changed?" id="ek">
            <Choice
              columns={1}
              value={kind}
              onChange={setKind}
              options={[
                { value: 'ttc', label: 'We have started trying to conceive' },
                { value: 'treatment', label: 'Fertility treatment has started' },
                { value: 'pregnancy', label: 'I am pregnant' },
                { value: 'resume', label: 'Back to the original plan' },
              ]}
            />
          </Field>
          <Field label="Anything you want noted?" id="en">
            <TextArea id="en" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          <Button
            className="w-full"
            disabled={busy || !kind}
            onClick={async () => {
              setBusy(true);
              try {
                const res = await api.post('/events', { kind, note });
                setMessage(res.message);
              } catch (err) {
                show(err.message, 'crit');
              } finally {
                setBusy(false);
              }
            }}
          >
            Save
          </Button>
        </>
      )}
    </Sheet>
  );
}

function NameSheet({ open, name, onClose, onSaved }) {
  const [value, setValue] = useState(name || '');
  const { show } = useToast();
  if (!open) return null;

  return (
    <Sheet open onClose={onClose} title="Your name">
      <Field label="What should the app call you?" id="nm">
        <TextInput id="nm" value={value} onChange={(e) => setValue(e.target.value)} autoFocus />
      </Field>
      <Button
        className="w-full"
        disabled={!value.trim()}
        onClick={() => {
          onSaved(value.trim());
          show('Saved.');
          onClose();
        }}
      >
        Save
      </Button>
    </Sheet>
  );
}

function numify(form, keys) {
  const out = { ...form };
  for (const k of keys) if (out[k] !== undefined && out[k] !== '') out[k] = Number(out[k]);
  return out;
}
